import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { debounce } from 'lodash';
import { 
  MagnifyingGlassIcon, 
  PlusIcon, 
  ChevronLeftIcon,
  TrashIcon,
  PencilSquareIcon,
  BeakerIcon,
  ScaleIcon,
  Bars3Icon
} from '@heroicons/react/24/solid';
import FoodSelectionModal from './FoodSelectionModal';
import { calculateFoodNutrients } from '../../utils/Nutrition';
import './RecipeSelectionModal.css';
import { suppressNumericInputContextMenu } from '../numericInputContextMenu';

const ItemTypes = { INGREDIENT: 'ingredient' };

// Draggable ingredient row component
const DraggableIngredient = ({ ing, idx, moveIngredient, onUpdate, onRemove, onFocus }) => {
  const ref = useRef(null);
  const [isLongPress, setIsLongPress] = useState(false);
  const longPressTimeout = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.INGREDIENT,
    item: () => ({ id: idx, index: idx }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    canDrag: () => isLongPress,
  });

  const [, drop] = useDrop({
    accept: ItemTypes.INGREDIENT,
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = idx;
      if (dragIndex === hoverIndex) return;
      
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      
      moveIngredient(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  useEffect(() => {
    drag(drop(ref));
  }, [drag, drop]);

  const handleTouchStart = () => {
    longPressTimeout.current = setTimeout(() => setIsLongPress(true), 300);
  };

  const handleTouchEnd = () => {
    if (longPressTimeout.current) clearTimeout(longPressTimeout.current);
    setTimeout(() => setIsLongPress(false), 100);
  };

  const handleMouseDown = () => {
    longPressTimeout.current = setTimeout(() => setIsLongPress(true), 300);
  };

  const handleMouseUp = () => {
    if (longPressTimeout.current) clearTimeout(longPressTimeout.current);
    setTimeout(() => setIsLongPress(false), 100);
  };

  return (
    <div
      ref={ref}
      className={`rsm-ingredient-edit-row ${isDragging ? 'dragging' : ''} ${isLongPress ? 'long-press' : ''}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="rsm-drag-handle">
        <Bars3Icon className="rsm-icon-sm" />
      </div>
      <span className="rsm-ing-name">{ing.name}</span>
      <input
        type="number"
        className="rsm-qty-input"
        value={ing.quantity}
        min="0"
        step="0.1"
        onFocus={onFocus}
        onContextMenu={suppressNumericInputContextMenu}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '' || val === null) {
            onUpdate(idx, 'quantity', '');
          } else {
            const quantity = Number(val);
            onUpdate(idx, 'quantity', Number.isFinite(quantity) && quantity >= 0 ? quantity : '');
          }
        }}
        onBlur={(e) => {
          const val = parseFloat(e.target.value);
          if (isNaN(val) || val < 0) {
            onUpdate(idx, 'quantity', 1);
          }
        }}
      />
      <select
        className="rsm-unit-select"
        value={ing.unit}
        onChange={(e) => onUpdate(idx, 'unit', e.target.value)}
      >
        <option value="serving">serving</option>
        <option value="g">g</option>
        <option value="ml">ml</option>
        <option value="oz">oz</option>
        <option value="cup">cup</option>
        <option value="tbsp">tbsp</option>
        <option value="tsp">tsp</option>
      </select>
      <button 
        className="rsm-remove-btn"
        onClick={() => onRemove(idx)}
      >
        <TrashIcon className="rsm-icon-sm" />
      </button>
    </div>
  );
};

const RecipeSelectionModal = ({ 
  close, 
  onSelect, 
  backendService, 
  modalService,
  manageMode = false,  // If true, show recipe management UI
  mealId = null,       // If provided, we're adding to a meal
  initialRecipe = null, // If provided, open directly to edit this recipe
  embedded = false
}) => {
  const [view, setView] = useState('list'); // 'list' | 'create' | 'edit' | 'scale'
  const [query, setQuery] = useState('');
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [scale, setScale] = useState(1);
  
  // Create/Edit form state
  const [recipeName, setRecipeName] = useState('');
  const [recipeDescription, setRecipeDescription] = useState('');
  const [ingredients, setIngredients] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const performFetch = useCallback(async (searchTerm, pageNum, isAppend) => {
    if (!isAppend) setLoading(true);
    
    try {
      const limit = 20;
      const data = await backendService.getRecipes(searchTerm, pageNum, limit);
      
      if (isAppend) {
        setRecipes(prev => {
          const existing = new Set(prev.map(r => r._id));
          const newItems = data.filter(r => !existing.has(r._id));
          return [...prev, ...newItems];
        });
      } else {
        setRecipes(data);
      }
      
      setHasMore(data.length === limit);
    } catch (err) {
      console.error("Failed to fetch recipes:", err);
    } finally {
      setLoading(false);
    }
  }, [backendService]);

  const debouncedFetch = useMemo(() => debounce((val) => {
    setPage(0);
    setHasMore(true);
    performFetch(val, 0, false);
  }, 500), [performFetch]);

  useEffect(() => {
    performFetch('', 0, false);
  }, [performFetch]);

  useEffect(() => {
    if (initialRecipe) {
        // Pre-load the recipe for editing
        setSelectedRecipe(initialRecipe);
        setRecipeName(initialRecipe.name);
        setRecipeDescription(initialRecipe.description || '');
        setIngredients(initialRecipe.ingredients || []);
        setView('edit');
    }
  }, [initialRecipe]);

  useEffect(() => {
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, debouncedFetch]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (!loading && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        performFetch(query, nextPage, true);
      }
    }
  };

  const handleSelectRecipe = (recipe) => {
    if (manageMode) {
      handleEditRecipe(recipe);
      return;
    }
    setSelectedRecipe(recipe);
    setScale(1);
    setView('scale');
  };

  const handleConfirmAdd = () => {
    if (selectedRecipe && onSelect) {
      onSelect(selectedRecipe._id, scale);
      close();
    }
  };

  const handleCreateNew = () => {
    setRecipeName('');
    setRecipeDescription('');
    setIngredients([]);
    setSelectedRecipe(null);
    setView('create');
  };

  const handleEditRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setRecipeName(recipe.name);
    setRecipeDescription(recipe.description || '');
    setIngredients(recipe.ingredients || []);
    setView('edit');
  };

  const handleDeleteRecipe = async (recipe) => {
    if (!window.confirm(`Delete recipe "${recipe.name}"?`)) return;
    
    try {
      await backendService.deleteRecipe(recipe._id);
      setRecipes(prev => prev.filter(r => r._id !== recipe._id));
    } catch (err) {
      alert('Failed to delete recipe');
    }
  };

  const handleSaveRecipe = async () => {
    if (!recipeName.trim()) {
      alert('Please enter a recipe name');
      return;
    }

    try {
      if (view === 'create') {
        const newRecipe = await backendService.createRecipe(
          recipeName.trim(),
          recipeDescription.trim(),
          ingredients.map(ing => ({
            food_id: ing.food_id,
            quantity: ing.quantity,
            unit: ing.unit
          }))
        );
        setRecipes(prev => [newRecipe, ...prev]);
      } else {
        const updated = await backendService.updateRecipe(selectedRecipe._id, {
          name: recipeName.trim(),
          description: recipeDescription.trim(),
          ingredients: ingredients.map(ing => ({
            food_id: ing.food_id,
            quantity: ing.quantity,
            unit: ing.unit,
            name: ing.name,
            brand: ing.brand,
            calories: ing.calories,
            protein: ing.protein,
            carbs: ing.carbs,
            fat: ing.fat,
            serving_size: ing.serving_size,
            serving_unit: ing.serving_unit,
            density: ing.density
          }))
        });
        setRecipes(prev => prev.map(r => r._id === updated._id ? updated : r));
      }
      setView('list');
    } catch (err) {
      alert('Failed to save recipe');
    }
  };

  const handleAddIngredient = () => {
    modalService.open(FoodSelectionModal, {
      backendService,
      onSelect: (foodId, quantity, unit) => {
        // We need to fetch the food details to snapshot
        backendService.getFoodById(foodId).then(food => {
          setIngredients(prev => [...prev, {
            food_id: foodId,
            quantity: quantity === '' || quantity === null || quantity === undefined ? 1 : quantity,
            unit: unit || 'serving',
            name: food?.name || 'Unknown',
            brand: food?.brand || '',
            calories: food?.calories || 0,
            protein: food?.protein || 0,
            carbs: food?.carbs || 0,
            fat: food?.fat || 0,
            serving_size: food?.serving_size || 100,
            serving_unit: food?.serving_unit || 'g',
            density: food?.density || 1
          }]);
        });
      }
    });
  };

  const handleRemoveIngredient = (index) => {
    setIngredients(prev => prev.filter((_, i) => i !== index));
  };

  const moveIngredient = useCallback((dragIndex, hoverIndex) => {
    setIngredients(prev => {
      const newIngredients = [...prev];
      const [dragged] = newIngredients.splice(dragIndex, 1);
      newIngredients.splice(hoverIndex, 0, dragged);
      return newIngredients;
    });
  }, []);

  // Unit conversion rates to grams
  const CONVERSION_RATES = {
    g: 1,
    kg: 1000,
    oz: 28.3495,
    lb: 453.592,
    ml: 1,  // Will be multiplied by density
    cup: 236.588,
    tbsp: 14.787,
    tsp: 4.929,
    serving: 1, // Special case - handled separately
  };

  const convertQuantity = (quantity, fromUnit, toUnit, ingredient) => {
    if (fromUnit === toUnit) return quantity;
    
    const density = ingredient.density || 1;
    const servingSize = ingredient.serving_size || 100;
    const servingUnit = ingredient.serving_unit || 'g';
    
    // First convert to grams
    let grams;
    if (fromUnit === 'serving') {
      const isVolumeUnit = ['ml', 'cup', 'tbsp', 'tsp'].includes(servingUnit);
      const rate = CONVERSION_RATES[servingUnit] || 1;
      grams = isVolumeUnit 
        ? quantity * servingSize * rate * density
        : quantity * servingSize * rate;
    } else {
      const isVolumeUnit = ['ml', 'cup', 'tbsp', 'tsp'].includes(fromUnit);
      const rate = CONVERSION_RATES[fromUnit] || 1;
      grams = isVolumeUnit ? quantity * rate * density : quantity * rate;
    }
    
    // Then convert from grams to target unit
    if (toUnit === 'serving') {
      const isVolumeUnit = ['ml', 'cup', 'tbsp', 'tsp'].includes(servingUnit);
      const rate = CONVERSION_RATES[servingUnit] || 1;
      const gramsPerServing = isVolumeUnit 
        ? servingSize * rate * density 
        : servingSize * rate;
      return gramsPerServing > 0 ? grams / gramsPerServing : quantity;
    } else {
      const isVolumeUnit = ['ml', 'cup', 'tbsp', 'tsp'].includes(toUnit);
      const rate = CONVERSION_RATES[toUnit] || 1;
      if (isVolumeUnit) {
        // grams -> ml -> target volume unit
        const ml = density > 0 ? grams / density : grams;
        return ml / rate;
      } else {
        return grams / rate;
      }
    }
  };

  const handleUpdateIngredient = (index, field, value) => {
    setIngredients(prev => prev.map((ing, i) => {
      if (i !== index) return ing;
      
      // If changing unit, also convert the quantity
      if (field === 'unit' && value !== ing.unit) {
        const parsedQuantity = Number(ing.quantity);
        const currentQty = Number.isFinite(parsedQuantity) && parsedQuantity >= 0 ? parsedQuantity : 1;
        const convertedQty = convertQuantity(currentQty, ing.unit, value, ing);
        return { 
          ...ing, 
          unit: value, 
          quantity: parseFloat(convertedQty.toFixed(2)) 
        };
      }
      
      return { ...ing, [field]: value };
    }));
  };

  const calculateTotals = (ings, scaleVal = 1) => {
    return ings.reduce((acc, ing) => {
      const parsedQuantity = Number(ing.quantity);
      const quantity = (Number.isFinite(parsedQuantity) && parsedQuantity >= 0 ? parsedQuantity : 1) * scaleVal;
      // Use the same serving basis and conversions as the meal preview.
      const nutrients = calculateFoodNutrients({ ...ing, quantity, unit: ing.unit || 'serving' });

      return {
        calories: acc.calories + nutrients.calories,
        protein: acc.protein + nutrients.protein,
        carbs: acc.carbs + nutrients.carbs,
        fat: acc.fat + nutrients.fat,
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  // RENDER: Scale selection view
  if (view === 'scale' && selectedRecipe) {
    const totals = calculateTotals(selectedRecipe.ingredients || [], scale);
    
    return (
      <div className={embedded ? "recipe-selection-embedded h-full" : "recipe-selection-modal"}>
        <button className="rsm-back-btn" onClick={() => setView('list')}>
          <ChevronLeftIcon className="rsm-icon-sm" /> Back
        </button>
        
        <h2 className="rsm-title">{selectedRecipe.name}</h2>
        {selectedRecipe.description && (
          <p className="rsm-description">{selectedRecipe.description}</p>
        )}
        
        <div className="rsm-scale-section">
          <label className="rsm-label">
            <ScaleIcon className="rsm-icon-sm" /> Scale Recipe
          </label>
          <div className="rsm-scale-controls">
            <button 
              className="rsm-scale-btn"
              onClick={() => setScale(Math.max(0.25, scale - 0.25))}
            >
              −
            </button>
            <span className="rsm-scale-value">{(scale * 100).toFixed(0)}%</span>
            <button 
              className="rsm-scale-btn"
              onClick={() => setScale(scale + 0.25)}
            >
              +
            </button>
          </div>
          <div className="rsm-scale-presets">
            <button onClick={() => setScale(0.5)}>½×</button>
            <button onClick={() => setScale(1)}>1×</button>
            <button onClick={() => setScale(1.5)}>1.5×</button>
            <button onClick={() => setScale(2)}>2×</button>
          </div>
        </div>
        
        <div className="rsm-ingredients-preview">
          <h4>Ingredients ({selectedRecipe.ingredients?.length || 0})</h4>
          {(selectedRecipe.ingredients || []).map((ing, idx) => (
            <div key={idx} className="rsm-ingredient-row">
              <span className="rsm-ing-name">{ing.name}</span>
              <span className="rsm-ing-qty">
                {(ing.quantity * scale).toFixed(2)} {ing.unit}
              </span>
            </div>
          ))}
        </div>
        
        <div className="rsm-nutrition-summary">
          <div className="rsm-nutr-item">
            <span className="rsm-nutr-val">{totals.calories.toFixed(0)}</span>
            <span className="rsm-nutr-label">kcal</span>
          </div>
          <div className="rsm-nutr-item">
            <span className="rsm-nutr-val">{totals.protein.toFixed(1)}g</span>
            <span className="rsm-nutr-label">Protein</span>
          </div>
          <div className="rsm-nutr-item">
            <span className="rsm-nutr-val">{totals.carbs.toFixed(1)}g</span>
            <span className="rsm-nutr-label">Carbs</span>
          </div>
          <div className="rsm-nutr-item">
            <span className="rsm-nutr-val">{totals.fat.toFixed(1)}g</span>
            <span className="rsm-nutr-label">Fat</span>
          </div>
        </div>
        
        <div className="rsm-actions">
          {!manageMode && (
            <button className="rsm-btn-primary" onClick={handleConfirmAdd}>
              Add to Meal
            </button>
          )}
          <button className="rsm-btn-secondary" onClick={() => handleEditRecipe(selectedRecipe)}>
            Edit Recipe
          </button>
        </div>
      </div>
    );
  }

  // RENDER: Create/Edit view
  if (view === 'create' || view === 'edit') {
    const totals = calculateTotals(ingredients);
    
    return (
      <div className={embedded ? "recipe-selection-embedded h-full" : "recipe-selection-modal"}>
        <button className="rsm-back-btn" onClick={() => setView('list')}>
          <ChevronLeftIcon className="rsm-icon-sm" /> Back
        </button>
        
        <h2 className="rsm-title">{view === 'create' ? 'Create Recipe' : 'Edit Recipe'}</h2>
        
        <div className="rsm-form">
          <input
            type="text"
            className="rsm-input"
            placeholder="Recipe name"
            value={recipeName}
            onChange={(e) => setRecipeName(e.target.value)}
            autoFocus
          />
          <textarea
            className="rsm-textarea"
            placeholder="Description (optional)"
            value={recipeDescription}
            onChange={(e) => setRecipeDescription(e.target.value)}
            rows={2}
          />
        </div>
        
        <div className="rsm-ingredients-section">
          <div className="rsm-section-header">
            <h4>Ingredients</h4>
            <button className="rsm-add-btn" onClick={handleAddIngredient}>
              <PlusIcon className="rsm-icon-sm" /> Add
            </button>
          </div>
          
          {ingredients.length === 0 ? (
            <p className="rsm-empty-msg">No ingredients added yet</p>
          ) : (
            <div className="rsm-ingredients-list">
                {ingredients.map((ing, idx) => (
                  <DraggableIngredient
                    key={ing.food_id || idx}
                    ing={ing}
                    idx={idx}
                    moveIngredient={moveIngredient}
                    onUpdate={handleUpdateIngredient}
                    onRemove={handleRemoveIngredient}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>
          )}
        </div>
        
        {ingredients.length > 0 && (
          <div className="rsm-nutrition-summary">
            <div className="rsm-nutr-item">
              <span className="rsm-nutr-val">{totals.calories.toFixed(0)}</span>
              <span className="rsm-nutr-label">kcal</span>
            </div>
            <div className="rsm-nutr-item">
              <span className="rsm-nutr-val">{totals.protein.toFixed(1)}g</span>
              <span className="rsm-nutr-label">Protein</span>
            </div>
            <div className="rsm-nutr-item">
              <span className="rsm-nutr-val">{totals.carbs.toFixed(1)}g</span>
              <span className="rsm-nutr-label">Carbs</span>
            </div>
            <div className="rsm-nutr-item">
              <span className="rsm-nutr-val">{totals.fat.toFixed(1)}g</span>
              <span className="rsm-nutr-label">Fat</span>
            </div>
          </div>
        )}
        
        <div className="rsm-actions">
          <button 
            className="rsm-btn-primary" 
            onClick={handleSaveRecipe}
            disabled={!recipeName.trim()}
          >
            {view === 'create' ? 'Create Recipe' : 'Save Changes'}
          </button>
          <button className="rsm-btn-secondary" onClick={() => setView('list')}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // RENDER: List view (default)
  return (
    <div className={embedded ? "recipe-selection-embedded h-full" : "recipe-selection-modal"}>
      {(!embedded) && (
        <div className="rsm-header">
          <h2 className="rsm-title">
            <BeakerIcon className="rsm-icon" /> Recipes
          </h2>
        </div>
      )}
      
      <div className="rsm-search-bar">
        <MagnifyingGlassIcon className="rsm-search-icon" />
        <input
          type="text"
          placeholder="Search recipes..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus={true}
        />
      </div>
      
      <div className="rsm-recipes-list" onScroll={handleScroll}>
        {loading && recipes.length === 0 ? (
          <div className="rsm-loading">Loading recipes...</div>
        ) : recipes.length === 0 ? (
          <div className="rsm-empty">
            <p>No recipes yet</p>
            <button className="rsm-btn-primary" onClick={handleCreateNew}>
              Create Your First Recipe
            </button>
          </div>
        ) : (
          recipes.map(recipe => (
            <div 
              key={recipe._id} 
              className="rsm-recipe-card"
              onClick={() => handleSelectRecipe(recipe)}
            >
              <div className="rsm-recipe-info">
                <span className="rsm-recipe-name">{recipe.name}</span>
                <span className="rsm-recipe-meta">
                  {recipe.ingredients?.length || 0} ingredients
                  {recipe.nutrition && ` • ${recipe.nutrition.calories?.toFixed(0) || 0} kcal`}
                </span>
              </div>
              <div className="rsm-recipe-actions" onClick={(e) => e.stopPropagation()}>
                <button 
                  className="rsm-action-btn"
                  onClick={() => handleEditRecipe(recipe)}
                  title="Edit"
                >
                  <PencilSquareIcon className="rsm-icon-sm" />
                </button>
                <button 
                  className="rsm-action-btn rsm-action-danger"
                  onClick={() => handleDeleteRecipe(recipe)}
                  title="Delete"
                >
                  <TrashIcon className="rsm-icon-sm" />
                </button>
              </div>
            </div>
          ))
        )}
        {loading && recipes.length > 0 && (
          <div className="rsm-loading-more">Loading more...</div>
        )}
      </div>
      
      <div className="rsm-footer rsm-search-footer">
        <button 
          className="rsm-create-footer-btn" 
          onClick={handleCreateNew}
        >
          <PlusIcon className="rsm-icon-sm" /> Create New Recipe
        </button>
      </div>
    </div>
  );
};

export default RecipeSelectionModal;
